import React from "react";
import { useQuery } from "react-query";
import { Megaphone } from "lucide-react";
import api from "../services/api";
import "./ReleaseNotesPage.css";

interface ReleaseNote {
  id: number;
  version: string;
  title: string;
  content: string;
  categories: string[];
  published_at: string;
  created_by_username: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  "New Features": "#1E40AF",
  "Bug Fixes": "#DC2626",
  Improvements: "#D97706",
  Security: "#059669",
  "Breaking Changes": "#7C3AED",
};

const ReleaseNotesPage: React.FC = () => {
  const { data, isLoading, isError } = useQuery(
    ["release-notes"],
    () => api.releaseNotes.getPublished(),
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const notes: ReleaseNote[] = data?.data || [];

  if (isLoading) {
    return (
      <div className="release-notes-page">
        <div className="release-notes-loading">Loading release notes...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="release-notes-page">
        <div className="release-notes-error">
          Failed to load release notes. Please try refreshing.
        </div>
      </div>
    );
  }

  return (
    <div className="release-notes-page">
      <div className="release-notes-header">
        <Megaphone size={28} />
        <div>
          <h1>What's New in HireFocuZ</h1>
          <p>Stay up to date with the latest features and improvements</p>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="release-notes-empty">
          <p>No release notes yet. Check back soon!</p>
        </div>
      ) : (
        <div className="release-notes-timeline">
          {notes.map((note) => (
            <div key={note.id} className="release-note-card">
              <div className="release-note-meta">
                <span className="release-note-version">v{note.version}</span>
                <span className="release-note-date">
                  {new Date(note.published_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <h2 className="release-note-title">{note.title}</h2>
              {note.categories.length > 0 && (
                <div className="release-note-categories">
                  {note.categories.map((cat) => (
                    <span
                      key={cat}
                      className="release-note-category-chip"
                      style={{
                        backgroundColor: CATEGORY_COLORS[cat] || "#6B7280",
                      }}
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              )}
              <div className="release-note-content">
                {note.content.split("\n").map((line, i) => {
                  const key = `${i}-${line.slice(0, 20)}`;
                  if (line.startsWith("## ")) {
                    return <h3 key={key}>{line.slice(3)}</h3>;
                  }
                  if (line.startsWith("- ")) {
                    return (
                      <p key={key} className="release-note-bullet">
                        {line}
                      </p>
                    );
                  }
                  if (line.trim() === "") return <br key={key} />;
                  return <p key={key}>{line}</p>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReleaseNotesPage;
